/**
 * L'elenco delle aziende nel pannello: logo, nome, città e livello, e a destra gli account e chi è al lavoro. Toccare
 * una riga sceglie l'azienda anche sulla mappa.
 */
import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { COLORE_LIVELLO } from './aspetto';
import type { FormatiMappa } from './formati';
import { LogoAzienda } from './LogoAzienda';
import { PallinoAdesso } from './PallinoAdesso';
import type { AziendaNellaVista } from './tipi';

interface Props {
  aziende: readonly AziendaNellaVista[];
  selezionata: string | null;
  onScegli: (chiave: string) => void;
  formati: FormatiMappa;
}

export function ordinaPerAccount(aziende: readonly AziendaNellaVista[]): AziendaNellaVista[] {
  return [...aziende].sort((a, b) => b.account_totale - a.account_totale || a.nome.localeCompare(b.nome));
}

export const ElencoAziende: React.FC<Props> = ({ aziende, selezionata, onScegli, formati }) => {
  const { t } = useTranslation('mappa');
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {aziende.map((a) => {
        const scelta = a.chiave === selezionata;
        const spenta = a.stato !== undefined && a.stato !== 'attiva';
        return (
          <Box component="li" key={a.chiave} sx={{ borderTop: `1px solid ${tokens.color.lineSoft}` }}>
            <ButtonBase
              onClick={() => onScegli(a.chiave)}
              aria-pressed={scelta}
              sx={{
                width: '100%',
                minHeight: 60,
                px: 2,
                py: 1.25,
                gap: 1.5,
                justifyContent: 'flex-start',
                textAlign: 'left',
                boxShadow: scelta ? `inset 3px 0 0 ${tokens.color.graphite}` : 'none',
                bgcolor: scelta ? tokens.color.bone : 'transparent',
                transition: 'background-color 120ms ease',
                '&:hover': { bgcolor: tokens.color.bone },
                '&.Mui-focusVisible': { outline: `2px solid ${tokens.color.graphite}`, outlineOffset: -2 },
              }}
            >
              <Box sx={{ opacity: spenta ? 0.5 : 1, display: 'flex' }}>
                <LogoAzienda nome={a.nome} logoUrl={a.logo_quadrato_url ?? a.logo_url} colore={a.colore} dimensione={36} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.88rem', color: tokens.color.graphite, lineHeight: 1.3 }}>
                  {a.nome}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, minWidth: 0 }}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: a.livello === 'free' ? 'transparent' : COLORE_LIVELLO[a.livello],
                      border: `1.5px solid ${COLORE_LIVELLO[a.livello]}`,
                    }}
                  />
                  <Typography noWrap sx={{ fontSize: '0.75rem', color: tokens.color.ash }}>
                    {[
                      a.citta,
                      t(`livelli.${a.livello}`),
                      a.sedi.length > 1 ? t('riepilogo.sedi', { count: a.sedi.length }) : null,
                      spenta && a.stato ? t(`stato_singolare.${a.stato}`) : null,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                  {formati.numero(a.account_totale)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: tokens.color.ash, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {t('riepilogo.al_lavoro', { attive: formati.numero(a.persone_attive) })}
                </Typography>
                {a.persone_adesso > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.6, mt: 0.25 }}>
                    <PallinoAdesso dimensione={6} />
                    <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: tokens.color.graphite, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {t('riepilogo.numero_adesso', { adesso: formati.numero(a.persone_adesso) })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
};
